import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, Plus, Edit2, Trash2, Upload, X, Download,
  FileText, CalendarIcon, ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";

import { api, authedUrl } from "@/lib/pp/api";
import { toLocalYMD, fromYMD, todayYMD } from "@/lib/pp/dates";
import { isAuthed } from "@/lib/pp/authSession";
import { OCP_LOOKUP_URL } from "@/lib/pp/constants";
import PharmacyFormDialog from "@/components/pp/PharmacyFormDialog";
import PatientFormDialog from "@/components/pp/PatientFormDialog";
import InvoicePreviewDialog from "@/components/pp/InvoicePreviewDialog";
import BDTracker from "@/components/pp/BDTracker";

const currency = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
  }).format(Number(n || 0));

export default function PPDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // BD mode gates whether the Financial tracker + Patients section render or
  // whether they're replaced by the BD tracker (attempts).
  const isBD = searchParams.get("mode") === "bd"
    || (typeof window !== "undefined" && window.sessionStorage.getItem("pp_mode") === "bd");
  const [pharmacy, setPharmacy] = useState(null);
  const [patients, setPatients] = useState([]);
  const [medications, setMedications] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [editForm, setEditForm] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [previewInvoicePatient, setPreviewInvoicePatient] = useState(null);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangeStats, setRangeStats] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [financialOpen, setFinancialOpen] = useState(false);
  const [patientFilter, setPatientFilter] = useState("all"); // all | pending_reg | completed_pay | pending_pay

  useEffect(() => {
    if (!isAuthed()) nav("/pharmacy/partners", { replace: true });
  }, [nav]);

  const load = useCallback(async () => {
    try {
      const [{ data: ph }, { data: pt }, { data: meds }, { data: sts }] =
        await Promise.all([
          api.get(`/pharmacies/${id}`),
          api.get(`/pharmacies/${id}/patients`),
          api.get(`/settings/medications`),
          api.get(`/settings/statuses`),
        ]);
      setPharmacy(ph);
      setPatients(pt.items || []);
      setMedications(meds.values || []);
      setStatuses(sts.values || []);
    } catch {
      toast.error("Failed to load");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addMedication = async (v) => {
    await api.post("/settings/medications", { value: v });
    const { data } = await api.get("/settings/medications");
    setMedications(data.values);
  };

  const deleteMedication = async (v) => {
    try {
      await api.delete(`/settings/medications/${encodeURIComponent(v)}`);
      const { data } = await api.get("/settings/medications");
      setMedications(data.values);
      toast.success(`Removed ${v}`);
    } catch { toast.error("Delete failed"); }
  };

  const addStatus = async (v) => {
    await api.post("/settings/statuses", { value: v });
    const { data } = await api.get("/settings/statuses");
    setStatuses(data.values);
  };

  const deletePharmacy = async () => {
    try {
      await api.delete(`/pharmacies/${id}`);
      toast.success("Pharmacy deleted");
      nav("/pharmacy/partners/dashboard");
    } catch { toast.error("Delete failed"); }
  };

  const patchPatient = async (patientId, patch) => {
    try {
      const patient = patients.find((p) => p.id === patientId);
      const merged = { ...patient, ...patch };
      await api.patch(`/patients/${patientId}`, merged);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deletePatient = async (pid) => {
    try {
      await api.delete(`/patients/${pid}`);
      toast.success("Patient deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const uploadInvoice = async (pid, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF only");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/patients/${pid}/invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice uploaded");
      load();
    } catch { toast.error("Upload failed"); }
  };

  const removeInvoice = async (pid) => {
    try {
      await api.delete(`/patients/${pid}/invoice`);
      toast.success("Invoice removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const runDateRange = async () => {
    if (!rangeStart || !rangeEnd) {
      toast.error("Pick both dates");
      return;
    }
    try {
      const { data } = await api.get(`/pharmacies/${id}/date-range-stats`, {
        params: {
          start: rangeStart,
          end: rangeEnd,
        },
      });
      setRangeStats(data);
    } catch { toast.error("Failed"); }
  };

  if (!pharmacy) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] py-10 px-6 text-[#4A5D55]">
        Loading…
      </div>
    );
  }

  return (
    <div
      data-testid="pp-detail"
      className="min-h-screen bg-[#F9F8F6] py-10 px-6"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <button
              onClick={() => nav("/pharmacy/partners/dashboard")}
              className="text-xs text-[#4A5D55] hover:text-[#1C2B26] inline-flex items-center gap-1 mb-1"
              data-testid="pp-detail-back"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Back to dashboard
            </button>
            <h1
              className="text-2xl font-semibold text-[#1C2B26] flex items-center gap-3"
              style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
            >
              {pharmacy.name}
              <Badge
                className={
                  pharmacy.status === "Active"
                    ? "bg-[#3B7A57] text-white"
                    : "bg-[#E5E2DC] text-[#4A5D55]"
                }
              >
                {pharmacy.status}
              </Badge>
            </h1>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-nowrap">
            {!isBD && (
            <Button
              size="sm"
              className="bg-[#0F3B2E] hover:bg-[#0A2A20] text-white px-3 sm:px-4"
              onClick={() => { setEditPatient(null); setShowPatientForm(true); }}
              data-testid="pp-add-patient"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add patient</span>
              <span className="sm:hidden">Patient</span>
            </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditForm(true)}
              data-testid="pp-edit-pharmacy"
              className="px-3 sm:px-4"
            >
              <Edit2 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-[#B84A4A] px-3 sm:px-4"
                  data-testid="pp-delete-pharmacy"
                >
                  <Trash2 className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete pharmacy permanently?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete the pharmacy and all its patients + invoices (cascade). This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deletePharmacy}
                    className="bg-[#B84A4A] hover:bg-[#9c3f3f] text-white"
                  >
                    Delete permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Contact (collapsible) */}
          <Card>
            <button
              type="button"
              onClick={() => setContactOpen((o) => !o)}
              data-testid="pp-toggle-contact"
              className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F9F8F6] transition-colors rounded-lg"
            >
              <h3 className="text-xs tracking-[0.24em] uppercase font-semibold text-[#2D5A4C]">
                Contact
              </h3>
              {contactOpen ? (
                <ChevronUp className="h-4 w-4 text-[#4A5D55]" />
              ) : (
                <ChevronDown className="h-4 w-4 text-[#4A5D55]" />
              )}
            </button>
            {contactOpen && (
              <CardContent className="pt-0 pb-5 px-5 space-y-2">
                <Row label="Email" val={pharmacy.email} />
                <Row label="Phone" val={pharmacy.phone} />
                <Row label="Fax" val={pharmacy.fax} />
                <Row label="OCP #" val={pharmacy.ocp_license} />
                <Row
                  label="Start date"
                  val={pharmacy.membership_date
                    ? format(fromYMD(pharmacy.membership_date), "PPP")
                    : "—"}
                />
                <Row
                  label="Main contact"
                  val={[pharmacy.contact_first_name, pharmacy.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || "—"}
                />
                <div className="text-sm">
                  <span className="text-[#4A5D55] mr-2">Address:</span>
                  <span className="text-[#1C2B26]">
                    {(() => {
                      const line1 = pharmacy.address || "";
                      const line2 = [
                        [pharmacy.city, pharmacy.province].filter(Boolean).join(", "),
                        pharmacy.postal_code,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      if (!line1 && !line2) return "—";
                      return (
                        <>
                          {line1}
                          {line1 && line2 && <br />}
                          {line2}
                        </>
                      );
                    })()}
                  </span>
                </div>
                {pharmacy.notes && <Row label="Notes" val={pharmacy.notes} />}
                <a
                  href={OCP_LOOKUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-[#2D5A4C] hover:text-[#1F4237] inline-flex items-center gap-1 pt-1"
                  data-testid="pp-detail-ocp-lookup"
                >
                  <ExternalLink className="h-3 w-3" /> OCP lookup
                </a>
              </CardContent>
            )}
          </Card>

          {/* Financial tracker → replaced with BD tracker when mode=bd. */}
          {isBD ? (
            <BDTracker pharmacyId={id} />
          ) : (
          <Card>
            <button
              type="button"
              onClick={() => setFinancialOpen((o) => !o)}
              data-testid="pp-toggle-financial"
              className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F9F8F6] transition-colors rounded-lg"
            >
              <h3 className="text-xs tracking-[0.24em] uppercase font-semibold text-[#2D5A4C]">
                Financial tracker
              </h3>
              {financialOpen ? (
                <ChevronUp className="h-4 w-4 text-[#4A5D55]" />
              ) : (
                <ChevronDown className="h-4 w-4 text-[#4A5D55]" />
              )}
            </button>
            {financialOpen && (
              <CardContent className="pt-0 pb-5 px-5 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Metric
                    label="Referred"
                    val={pharmacy.patient_count}
                    active={patientFilter === "all"}
                    onClick={() => setPatientFilter("all")}
                    tid="pp-filter-referred"
                  />
                  <Metric
                    label="Pending"
                    val={pharmacy.pending_registrations}
                    active={patientFilter === "pending_reg"}
                    onClick={() => setPatientFilter("pending_reg")}
                    tid="pp-filter-pending-reg"
                  />
                  <Metric
                    label="Completed payments"
                    val={pharmacy.completed_payments}
                    active={patientFilter === "completed_pay"}
                    onClick={() => setPatientFilter("completed_pay")}
                    tid="pp-filter-completed-pay"
                  />
                  <Metric
                    label="Pending payments"
                    val={pharmacy.pending_payments}
                    tone="danger"
                    active={patientFilter === "pending_pay"}
                    onClick={() => setPatientFilter("pending_pay")}
                    tid="pp-filter-pending-pay"
                  />
                </div>
                <div className="pt-3 border-t border-[#E5E2DC] space-y-2">
                  <div className="text-[11px] tracking-[0.24em] uppercase font-semibold text-[#4A5D55]">
                    Custom date range
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <DateBtn label="Start" val={rangeStart} onChange={setRangeStart} tid="pp-range-start" />
                    <DateBtn label="End" val={rangeEnd} onChange={setRangeEnd} tid="pp-range-end" />
                    <Button size="sm" onClick={runDateRange} data-testid="pp-range-apply">Apply</Button>
                  </div>
                  {rangeStats && (
                    <div className="text-xs text-[#4A5D55] pt-1">
                      {rangeStats.count} patient(s) · pending {currency(rangeStats.fees_pending)} · reimbursed {currency(rangeStats.fees_reimbursed)}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
          )}
        </div>

        {/* Patients — Partners mode only. BD mode uses attempts inside the BD tracker above. */}
        {!isBD && (
        <>
        <div className="flex items-center justify-between mt-6 flex-wrap gap-2">
          <h2
            className="text-lg font-semibold text-[#1C2B26]"
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
          >
            Patients
          </h2>
          {patientFilter !== "all" && (
            <button
              type="button"
              onClick={() => setPatientFilter("all")}
              data-testid="pp-filter-clear"
              className="text-xs inline-flex items-center gap-1.5 bg-[#2D5A4C]/10 border border-[#2D5A4C]/25 text-[#2D5A4C] rounded-full px-3 py-1 hover:bg-[#2D5A4C]/20 transition-colors"
            >
              Filter:&nbsp;
              <span className="font-semibold">
                {patientFilter === "pending_reg" && "Pending"}
                {patientFilter === "completed_pay" && "Completed payments"}
                {patientFilter === "pending_pay" && "Pending payments"}
              </span>
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="bg-white border border-[#E5E2DC] rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F9F8F6] text-[#4A5D55] text-[11px] uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-4 py-2">First</th>
                <th className="text-left px-4 py-2">Last</th>
                <th className="text-left px-4 py-2">ID</th>
                <th className="text-left px-4 py-2">Registration</th>
                <th className="text-center px-4 py-2">Paid</th>
                <th className="text-center px-4 py-2">Pending</th>
                <th className="text-left px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E2DC]">
              {patients
                .filter((p) => {
                  if (patientFilter === "pending_reg") return !p.registration_sent;
                  if (patientFilter === "completed_pay")
                    return (p.record_count || 0) > 0 && !(p.records_pending_pay || 0);
                  if (patientFilter === "pending_pay") return (p.records_pending_pay || 0) > 0;
                  return true;
                })
                .map((p) => (
                <tr
                  key={p.id}
                  data-testid={`pp-patient-row-${p.id}`}
                  className="hover:bg-[#F9F8F6] cursor-pointer select-none"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                  onClick={() => nav(`/pharmacy/partners/${id}/patients/${p.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#1C2B26] whitespace-nowrap">
                      {p.first_name || (p.patient_name || "").split(" ")[0] || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#1C2B26] whitespace-nowrap">
                      {p.last_name || (p.patient_name || "").split(" ").slice(1).join(" ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#1C2B26] whitespace-nowrap">{p.client_id}</td>
                  <td className="px-4 py-3">
                    {p.registration_sent ? (
                      <span className="text-sm text-[#1C2B26] whitespace-nowrap">
                        {p.registration_sent_date
                          ? format(fromYMD(p.registration_sent_date), "MMM d, yyyy")
                          : "—"}
                      </span>
                    ) : (
                      <span className="text-sm text-[#C97757] whitespace-nowrap">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-[#2D5A4C] whitespace-nowrap">
                    {currency(p.fees_paid || 0)}
                  </td>
                  <td
                    className={`px-4 py-3 text-center text-sm whitespace-nowrap ${
                      (p.fees_pending || 0) > 0 ? "text-[#B84A4A]" : "text-[#1C2B26]"
                    }`}
                  >
                    {currency(p.fees_pending || 0)}
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => nav(`/pharmacy/partners/${id}/patients/${p.id}`)}
                        className="text-xs text-[#2D5A4C] hover:underline whitespace-nowrap"
                        data-testid={`pp-patient-view-${p.id}`}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => { setEditPatient(p); setShowPatientForm(true); }}
                        className="text-xs text-[#4A5D55]"
                        data-testid={`pp-patient-edit-${p.id}`}
                        aria-label="Edit patient"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deletePatient(p.id)}
                        className="text-xs text-[#B84A4A]"
                        data-testid={`pp-patient-delete-${p.id}`}
                        aria-label="Delete patient"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!patients.filter((p) => {
                if (patientFilter === "pending_reg") return !p.registration_sent;
                if (patientFilter === "completed_pay")
                  return (p.record_count || 0) > 0 && !(p.records_pending_pay || 0);
                if (patientFilter === "pending_pay") return (p.records_pending_pay || 0) > 0;
                return true;
              }).length && (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-left text-sm text-[#4A5D55]">
                    {patientFilter === "all"
                      ? "No patients yet."
                      : "No patients match this filter."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      <PharmacyFormDialog
        open={editForm}
        onOpenChange={setEditForm}
        editing={pharmacy}
        statuses={statuses}
        onAddStatus={addStatus}
        onSaved={load}
        mode={isBD ? "bd" : "partners"}
      />

      <PatientFormDialog
        open={showPatientForm}
        onOpenChange={setShowPatientForm}
        editing={editPatient}
        pharmacyId={id}
        onSaved={load}
      />
    </div>
  );
}

const Row = ({ label, val }) => (
  <div className="text-sm">
    <span className="text-[#4A5D55] mr-2">{label}:</span>
    <span className="text-[#1C2B26]">{val || "—"}</span>
  </div>
);

const Metric = ({ label, val, tone, active, onClick, tid }) => {
  const isDanger = tone === "danger";
  const base =
    "text-left rounded p-2 transition-all border w-full focus:outline-none";
  const inactive =
    "bg-[#F9F8F6] border-transparent hover:border-[#2D5A4C]/25 hover:bg-white cursor-pointer";
  const activeCls = isDanger
    ? "bg-[#B84A4A]/8 border-[#B84A4A]/45 ring-1 ring-[#B84A4A]/30"
    : "bg-[#2D5A4C]/8 border-[#2D5A4C]/45 ring-1 ring-[#2D5A4C]/30";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      aria-pressed={active}
      className={`${base} ${active ? activeCls : inactive}`}
    >
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#4A5D55]">{label}</div>
      <div
        className={`text-xl font-semibold ${
          isDanger ? "text-[#B84A4A]" : "text-[#1C2B26]"
        }`}
      >
        {val ?? "—"}
      </div>
    </button>
  );
};

const DateBtn = ({ label, val, onChange, tid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-xs bg-white border border-[#E5E2DC] rounded px-2 py-1 flex items-center gap-1 text-[#1C2B26]"
          data-testid={tid}
        >
          <CalendarIcon className="h-3 w-3 text-[#4A5D55]" />
          {val ? format(fromYMD(val), "MMM d, yyyy") : label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={fromYMD(val)}
          onSelect={(d) => {
            onChange(toLocalYMD(d));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
